import { Avatar, Box, Stack, Typography, useTheme } from '@mui/material'
import { useState } from 'react'
import { Fade, IconButton } from 'utoniq-ui'

import {
  ItemThumbnailType,
  PortfolioConnectionFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout } from '@/components/layouts'
import { FullscreenModal } from '@/components/organisms/functionals'
import { ItemReversibleThumbnail } from '@/components/organisms/item/ItemReversableThumbnail'
import { SoundPlayer } from '@/components/organisms/resourcePlayer/SoundPlayer'
import { ThumbnailActionAreaCell } from '@/components/pages/OwnedItemDetail/template/components/ThumbnailActionArea'
import { useTranslation } from '@/core'
import { CellFrame, Organism, OrganismFrame } from '@/core/architecture'

import type { State } from '../../../viewModel'
import { ItemDescriptionCell } from '../ItemDescription'
import { SupplementLinkSetCell } from '../SupplementLinkSet'

export type Props = {
  loading: boolean
  portfolio: State['portfolio']
  isFlipped: boolean
  onReverseThumbnail: () => void
  onClickProtectUserItem: () => void
  onOpenFullScreen?: () => void
  onOpenMultipleImage?: () => void
}

export const OwnedItemDetailAudioView: Organism<Props> = ({
  loading,
  portfolio,
  isFlipped,
  onClickProtectUserItem,
  onReverseThumbnail,
  onOpenFullScreen,
  onOpenMultipleImage,
}) => {
  const [displayResource, setDisplayResource] = useState(false)
  const onClickDisplayResource = () => setDisplayResource(prev => !prev)

  return (
    <CellFrame
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: '1 0 auto',
      }}
    >
      <Stack
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        flex={1}
      >
        <Stack
          alignItems="center"
          sx={{
            position: 'relative',
            '.MuiButtonBase-root': {
              height: 'fit-content',
            },
          }}
        >
          <ThumbnailActionAreaCell
            onOpenFullScreen={onOpenFullScreen}
            onOpenMultipleImage={onOpenMultipleImage}
            numberImage={portfolio?.item.images?.length ?? 0}
          />
          <ItemReversibleThumbnail
            loading={loading}
            flipProps={{
              isFlipped,
            }}
            btnPlay={{
              type: 'back',
              onClick: onClickDisplayResource,
            }}
            imageAreaProps={{
              thumbnail: portfolio?.item?.thumbnail,
              size:
                portfolio?.item.thumbnailType === ItemThumbnailType.Square
                  ? 'x-large-square'
                  : 'x-large',
              onClick: onReverseThumbnail,
            }}
          />
        </Stack>
        <ItemDescriptionCell portfolio={portfolio} />

        <Box sx={{ mt: 4, mb: 2 }}>
          {isFlipped ? (
            <IconButton.Rotate
              size="small"
              onClick={onReverseThumbnail}
              bgcolor="primary.main"
              color="white"
            />
          ) : (
            <IconButton.AudioOn
              size="small"
              onClick={onReverseThumbnail}
              bgcolor="primary.main"
              color="white"
            />
          )}
        </Box>
      </Stack>

      <AudioPlayModal
        open={displayResource}
        onClose={onClickDisplayResource}
        item={portfolio?.item}
      />

      <SupplementLinkSetCell
        sku={portfolio?.item.sku}
        itemId={portfolio?.item.id}
        commentCount={portfolio?.item.commentCount}
        relatedMissions={portfolio?.item.relatedMissions}
        artistId={portfolio?.item.issuedArtist?.id ?? ''}
        isUserProtected={portfolio?.isUserProtected}
        issuableNft={!!portfolio?.item.nft?.chains}
        onClickProtectUserItem={onClickProtectUserItem}
      />
    </CellFrame>
  )
}
type AudioPlayModal = {
  open: boolean
  item?: PortfolioConnectionFragment['item']
  onClose: () => void
}
export const AudioPlayModal: Organism<AudioPlayModal> = ({
  open,
  item,
  onClose,
}) => {
  const { t } = useTranslation('ownedItemDetail')

  return (
    <FullscreenModal
      sx={{ display: open ? 'block' : 'none' }}
      open
      onClose={onClose}
    >
      <BaseLayout
        header={{
          left: (
            <IconButton.Close edge="start" size="medium" onClick={onClose} />
          ),
          type: 'modal',
        }}
      >
        <Fade style={{ height: '100%' }}>
          {item ? (
            <AudioPlayCell item={item} open={open} />
          ) : (
            <Typography variant="body1" sx={{ textAlign: 'center' }}>
              {t('itemNotFound')}
            </Typography>
          )}
        </Fade>
      </BaseLayout>
    </FullscreenModal>
  )
}

export type AudioPlayCellProps = {
  item?: PortfolioConnectionFragment['item']
  open: boolean
}
export const AudioPlayCell: Organism<AudioPlayCellProps> = ({ item, open }) => {
  return (
    <OrganismFrame
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: '1 0 auto',
        height: '100%',
        bgcolor: 'background.default',
        px: 5,
      }}
    >
      <Stack display="flex" flexDirection="column" sx={{ height: '100%' }}>
        <Stack
          display="flex"
          flexDirection="column"
          alignItems="center"
          flex={1}
        >
          <Avatar
            variant="square"
            src={item?.thumbnail?.front?.mediumUri ?? ''}
            sx={{
              width: 146,
              height: 146,
              mt: '80px',
              mb: 8,
              img: {
                objectFit: 'contain',
              },
              borderRadius: 1,
            }}
          />
          <SoundGraph />
        </Stack>
        <CellFrame sx={{ position: 'relative', py: 6 }}>
          {item?.resource && (
            <SoundPlayer resource={item?.resource} autoplay={open} />
          )}
        </CellFrame>
      </Stack>
    </OrganismFrame>
  )
}

/**
 * @description
 * Sound Visualizer用SVG
 * 仕様策定中
 */
const SoundGraph = () => {
  const { palette } = useTheme()

  return (
    <svg
      width="250"
      height="84"
      viewBox="0 0 250 84"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      color={palette.grey[100]}
    >
      <rect y="39" width="2" height="6" fill="currentColor" />
      <rect x="8" y="39" width="2" height="6" fill="currentColor" />
      <rect x="16" y="39" width="2" height="6" fill="currentColor" />
      <rect x="24" y="39" width="2" height="6" fill="currentColor" />
      <rect x="32" y="37.5" width="2" height="9" fill="currentColor" />
      <rect x="40" y="36" width="2" height="12" fill="currentColor" />
      <rect x="48" y="36" width="2" height="12" fill="currentColor" />
      <rect x="56" y="36" width="2" height="12" fill="currentColor" />
      <rect x="64" y="36" width="2" height="12" fill="currentColor" />
      <rect x="72" y="28" width="2" height="28" fill="currentColor" />
      <rect x="80" y="20" width="2" height="44" fill="currentColor" />
      <rect x="88" y="16" width="2" height="52" fill="currentColor" />
      <rect x="96" y="12" width="2" height="60" fill="currentColor" />
      <rect x="104" width="2" height="84" fill="currentColor" />
      <rect x="112" width="2" height="84" fill="currentColor" />
      <rect x="120" y="1.5" width="2" height="81" fill="currentColor" />
      <rect x="128" y="9" width="2" height="66" fill="currentColor" />
      <rect x="136" y="12" width="2" height="60" fill="currentColor" />
      <rect x="144" y="16" width="2" height="52" fill="currentColor" />
      <rect x="152" y="24" width="2" height="36" fill="currentColor" />
      <rect x="160" y="32" width="2" height="20" fill="currentColor" />
      <rect x="168" y="37.5" width="2" height="9" fill="currentColor" />
      <rect x="176" y="36" width="2" height="12" fill="currentColor" />
      <rect x="184" y="38.5" width="2" height="7" fill="currentColor" />
      <rect x="192" y="29.5" width="2" height="25" fill="currentColor" />
      <rect x="200" y="21.5" width="2" height="41" fill="currentColor" />
      <rect x="208" y="25.5" width="2" height="33" fill="currentColor" />
      <rect x="216" y="25.5" width="2" height="33" fill="currentColor" />
      <rect x="224" y="25.5" width="2" height="33" fill="currentColor" />
      <rect x="232" y="29.5" width="2" height="25" fill="currentColor" />
      <rect x="240" y="37.5" width="2" height="9" fill="currentColor" />
      <rect x="248" y="36" width="2" height="12" fill="currentColor" />
    </svg>
  )
}
